import { getAggregatedSymbols, getRevenueAnalytics } from './http/labRevenueAnalytics';
import type { GetAggregatedSymbolsOptions, GetRevenueAnalyticsOptions } from './http/labRevenueAnalytics';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type { AggregatedSymbolsRawRow, RevenueAnalyticsRawRow } from 'src/admin/LAB/RevenueAnalytics/types';

/** QueryKey constant for provider revenue analytics. */
export const LAB_REVENUE_ANALYTICS = 'lab-revenue-analytics';

/** QueryKey constant for the per-symbol aggregation. */
export const LAB_AGGREGATED_SYMBOLS = 'lab-aggregated-symbols';

/** Provider segment of the key: order-insensitive over the ids, `null` for the aggregate. */
export const revenueAnalyticsProviderKey = (providerIds: ReadonlyArray<string> = []): string | null =>
    providerIds.length === 0 ? null : [...providerIds].sort().join(',');

/**
 * Query for one page of provider revenue analytics. With no
 * `providerIds` it loads the aggregate across all providers; with some it
 * narrows to those providers.
 *
 * `providerIds`, `firmId`, `symbol`, `filter`, `orderby`, `skip`, and `top` are all
 * part of the key so distinct provider / firm / filter / sort / page / search slices don't
 * clobber each other, and one firm's slices can be invalidated together via the
 * `[LAB_REVENUE_ANALYTICS, revenueAnalyticsProviderKey(providerIds), firmId ?? null]` prefix. Toggling superuser needs
 * no key dimension: QueryCacheUserService resets user-scoped queries when
 * the effective identity changes. Callers own the `enabled` gate (the
 * scope must be known before the query runs).
 */
export const getRevenueAnalyticsQuery = (
    options: GetRevenueAnalyticsOptions = {},
): QueryObserverOptions<RevenueAnalyticsRawRow[], Error, RevenueAnalyticsRawRow[]> => {
    const { providerIds, firmId, filter, skip, top, orderby, symbol } = options;
    return {
        queryKey: [
            LAB_REVENUE_ANALYTICS,
            revenueAnalyticsProviderKey(providerIds),
            firmId ?? null,
            symbol ?? null,
            filter ?? null,
            orderby ?? null,
            skip ?? null,
            top ?? null,
        ],
        queryFn: () => getRevenueAnalytics(options),
    };
};

/**
 * Query for one page of the per-symbol aggregation backing the Aggregated
 * Symbols tab. Keyed like {@link getRevenueAnalyticsQuery} (minus the
 * provider dimension, which does not apply to symbol rows); one firm's
 * slices are invalidated together via the
 * `[LAB_AGGREGATED_SYMBOLS, firmId ?? null]` prefix. Callers own the
 * `enabled` gate.
 */
export const getAggregatedSymbolsQuery = (
    options: GetAggregatedSymbolsOptions = {},
): QueryObserverOptions<AggregatedSymbolsRawRow[], Error, AggregatedSymbolsRawRow[]> => {
    const { firmId, filter, skip, top, orderby, symbol } = options;
    return {
        queryKey: [
            LAB_AGGREGATED_SYMBOLS,
            firmId ?? null,
            symbol ?? null,
            filter ?? null,
            orderby ?? null,
            skip ?? null,
            top ?? null,
        ],
        queryFn: () => getAggregatedSymbols(options),
    };
};
